import styled from 'styled-components';
import type { LineMonitoringBarStatus } from './LineMonitoringCard.types';

export const CardOuter = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

export const SummaryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const MetaBadge = styled.span`
  font-size: 12px;
  line-height: 1.2;
  color: var(--allie-color-neutral-50);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 70%;
`;

export const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const PlantLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--allie-color-neutral-50);
`;

export const LineTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--allie-color-neutral-80);
  line-height: 1.25;
`;

export const ValuesRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px;
`;

export const ProducedValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: var(--allie-color-neutral-80);
`;

export const TargetText = styled.span`
  font-size: 14px;
  color: var(--allie-color-neutral-50);
`;

const completionColors = {
  success: 'var(--allie-color-success-main)',
  warning: 'var(--allie-color-warning-main)',
  error: 'var(--allie-color-error-main)',
};

export const CompletionBadge = styled.span<{ $level: 'success' | 'warning' | 'error' }>`
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9999px;
  color: ${(p) => completionColors[p.$level]};
  background: var(--allie-color-neutral-5);
  border: 1px solid var(--allie-color-neutral-20);
`;

export const MiniChartWrap = styled.div`
  position: relative;
  width: 100%;
`;

export const MiniChart = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 3px;
  height: 72px;
  width: 100%;
`;

export const MiniBarCol = styled.div`
  position: relative;
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
`;

const barColor: Record<LineMonitoringBarStatus, string> = {
  success: 'var(--allie-color-success-main)',
  warning: 'var(--allie-color-warning-main)',
  none: 'var(--allie-color-neutral-20)',
};

export const TargetMark = styled.div<{ $heightPct: number }>`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  border-top: 2px dashed var(--allie-color-error-main);
  height: ${(p) => p.$heightPct}%;
  pointer-events: none;
  z-index: 1;
`;

export const TargetMinMark = styled(TargetMark)`
  border-top: 2px dashed var(--allie-color-warning-main);
`;

export const ActualBar = styled.div<{ $heightPct: number; $status: LineMonitoringBarStatus }>`
  width: 100%;
  max-width: 12px;
  height: ${(p) => p.$heightPct}%;
  min-height: ${(p) => (p.$heightPct > 0 ? '2px' : '0')};
  border-radius: 3px 3px 0 0;
  background: ${(p) => barColor[p.$status]};
  z-index: 2;
`;

export const HourLabelsRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 3px;
  margin-top: 4px;
`;

export const HourLabel = styled.span`
  flex: 1;
  text-align: center;
  font-size: 9px;
  color: var(--allie-color-neutral-50);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ExpandToggleRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  margin-top: 12px;
  padding: 8px 0 0;
  border: none;
  border-top: 1px solid var(--allie-color-neutral-10);
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--allie-color-neutral-60);

  &:hover {
    color: var(--allie-color-neutral-80);
  }
`;

export const NoDataText = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: var(--allie-color-neutral-40);
`;

export const ExpandedSection = styled.div<{ $open: boolean }>`
  overflow: hidden;
  max-height: ${(p) => (p.$open ? '600px' : '0')};
  transition: max-height 0.25s ease;
  /* inner padding so content isn't flush during close animation */
  padding-top: ${(p) => (p.$open ? '12px' : '0')};
`;
